/// <reference types="vite/types/importMeta.d.ts" />
import '../../types/global.d.ts';

const defaultDebounceDelayMs = 500;
const millisecondsPerSecond = 1000;

export interface Tracker<TEvent extends string> {
  track(eventName: TEvent, details?: EventDetails): void;
  trackDebounced(eventName: TEvent, details?: EventDetails): void;
  createDebouncedTrack(
    delayMs?: number
  ): (eventName: TEvent, details?: EventDetails) => void;
  startTimedEvent(
    eventId: string,
    eventName: TEvent,
    details?: EventDetails
  ): void;
  updateTimedEvent(eventId: string, details: EventDetails): void;
  endTimedEvent(eventId: string, details?: EventDetails): void;
  cancelTimedEvent(eventId: string): void;
}

type TimedEvent<TEvent> = {
  start: number;
  name: TEvent;
  details: EventDetails;
};

export function createTracker<TEvent extends string>(
  eventPrefix: string
): Tracker<TEvent> {
  type DebouncedTrackState = {
    timeoutId: ReturnType<typeof setTimeout> | null;
    pendingDetails: EventDetails | undefined;
  };

  const debouncedTrackStore = new Map<TEvent, DebouncedTrackState>();
  const timedEventStore = new Map<string, TimedEvent<TEvent>>();

  function track(eventName: TEvent, details?: EventDetails): void {
    if (typeof window.aptrinsic === 'function') {
      if (import.meta.env.DEV) {
        console.log('%cANALYTICS', 'color:green', eventName, details);
      }
      window.aptrinsic('track', eventPrefix + eventName, details);
    }
  }

  function createDebouncedTrack(
    delayMs: number = defaultDebounceDelayMs
  ): (eventName: TEvent, details?: EventDetails) => void {
    return (eventName: TEvent, details?: EventDetails): void => {
      const state = debouncedTrackStore.get(eventName);

      if (state?.timeoutId) {
        clearTimeout(state.timeoutId);
      }

      const newState: DebouncedTrackState = {
        timeoutId: setTimeout(() => {
          const finalState = debouncedTrackStore.get(eventName);
          if (finalState) {
            track(eventName, finalState.pendingDetails);
            debouncedTrackStore.delete(eventName);
          }
        }, delayMs),
        pendingDetails: details,
      };

      debouncedTrackStore.set(eventName, newState);
    };
  }

  function startTimedEvent(
    eventId: string,
    eventName: TEvent,
    details?: EventDetails
  ): void {
    timedEventStore.set(eventId, {
      start: performance.now(),
      name: eventName,
      details: details ?? {},
    });
  }

  function updateTimedEvent(eventId: string, details: EventDetails): void {
    const event = timedEventStore.get(eventId);
    if (event != undefined) {
      event.details = { ...event.details, ...details };
    }
  }

  function endTimedEvent(eventId: string, details?: EventDetails): void {
    const event = timedEventStore.get(eventId);
    if (event != undefined) {
      const duration =
        (performance.now() - event.start) / millisecondsPerSecond;
      track(event.name, { ...event.details, ...details, duration });
      timedEventStore.delete(eventId);
    }
  }

  function cancelTimedEvent(eventId: string): void {
    timedEventStore.delete(eventId);
  }

  const trackDebounced = createDebouncedTrack();

  return {
    track,
    trackDebounced,
    createDebouncedTrack,
    startTimedEvent,
    updateTimedEvent,
    endTimedEvent,
    cancelTimedEvent,
  };
}
